/* eslint-disable @typescript-eslint/no-empty-function */
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { InfoTag } from 'shared/ui/InfoTag';
import { WarnTag } from 'shared/ui/WarnTag';
import { ReactComponent as BillIcon } from 'shared/assets/icons/BillIcon.svg';
import { ReactComponent as NonePaidDays } from 'shared/assets/icons/NonePaidDays.svg';
import { ReactComponent as SquareIcon } from 'shared/assets/icons/SquareIcon.svg';
import { ReactComponent as AccessIcon } from 'shared/assets/icons/AccessIcon.svg';
import { ReactComponent as ContractWhiteIcon } from 'shared/assets/icons/ContractWhiteIcon.svg';
import { ReactComponent as AccessDeniedIcon } from 'shared/assets/icons/AccessDeniedIcon.svg';
import { ReactComponent as ContractIcon } from 'shared/assets/icons/ContractIcon.svg';
import { ReactComponent as KeyIcon } from 'shared/assets/icons/KeyIcon.svg';
import { ReactComponent as CancelIcon } from 'shared/assets/icons/CancelIcon.svg';
import { Button } from 'shared/ui/Button';
import { CachedBox, LockStatusMarker, useWatchLockState } from 'entities/Box';
import { Reservation } from 'entities/Reservation';
import { getClientDateFormat, getServerDateWithMinuteTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { useGetCurrencySymbol } from 'app/appState';
import dayjs from 'dayjs';
import { cancelReservationModalActions } from 'features/CancelReservationModal';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { takeUpReservationModalActions } from 'widgets/TakeUpReservationModal';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { OpenBox } from 'features/OpenBox';
import {
  ContractWithBookingInfo,
  UnitType,
  getNotifiedPriceRaise,
  getPriceWithVat,
  useGetContractItemPriceRaisesQuery,
} from 'entities/Contract';
import { useGetVatRateQuery } from 'entities/VatRate';
import { UserAccountType } from 'entities/User';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { useGetContractVerificationDocumentsQuery, VerificationFileStatus } from 'entities/VerificationPhoto';
import { MoveOutPhotosModal } from 'widgets/MoveOutPhotosModal';
import { BoxHistoryActionNames, useGetPaginatedHistoryQuery } from 'entities/History';

interface BoxCardProps {
  box: Nullable<CachedBox>;
  type: 'reservation' | 'contract';
  onOpenContractDetails: (contractId: string) => void;
  reservation?: Nullable<Reservation>;
  warehouseId?: string;
  contractsData?: ContractWithBookingInfo[];
}

export const BoxCard: FC<BoxCardProps> = memo((props) => {
  const { box, type, reservation, warehouseId, contractsData, onOpenContractDetails } = props;
  const isReservation = type === 'reservation';

  const dispatch = useAppDispatch();

  const currencySymbol = useGetCurrencySymbol();

  const { t } = useAppTranslation(['contracts', 'common']);

  const lockStatus = useWatchLockState(box, warehouseId);

  const [isMoveOutPhotosOpen, setMoveOutPhotosOpen] = useState(false);

  const openMoveOutPhotosModal = useCallback((): void => {
    setMoveOutPhotosOpen(true);
  }, []);

  const closeMoveOutPhotosModal = useCallback((): void => {
    setMoveOutPhotosOpen(false);
  }, []);

  const contractWithBookingInfo = useMemo(
    () => contractsData?.find((contractData) => contractData.contractId === box?.contract?.contractId),
    [box?.contract?.contractId, contractsData],
  );

  const { data: priceRaises } = useGetContractItemPriceRaisesQuery(
    { contractId: contractWithBookingInfo?.contractId, limit: 50 },
    { skip: isReservation || !contractWithBookingInfo?.contractId },
  );
  const notifiedPriceRaise = useMemo(() => getNotifiedPriceRaise(priceRaises?.content), [priceRaises?.content]);
  const rentUnitType = box?.unitType === UnitType.OFFICE ? UnitType.OFFICE : UnitType.BOX;
  const rentAccountType = notifiedPriceRaise?.contract?.user?.accountType;

  const { data: boxVatRate } = useGetVatRateQuery(
    { accountType: rentAccountType as UserAccountType, unitType: rentUnitType },
    { skip: !rentAccountType || !notifiedPriceRaise?.priceRaiseDate },
  );

  const { data: moveOutDocs = [] } = useGetContractVerificationDocumentsQuery(
    { contractId: contractWithBookingInfo?.contractId },
    { skip: !contractWithBookingInfo?.contractId },
  );

  const { data: lastBoxOpenings } = useGetPaginatedHistoryQuery(
    {
      boxId: box?.originalEntityId,
      orderBy: 'createdAt:DESC',
      limit: 2,
      actionName: [BoxHistoryActionNames.box_open, BoxHistoryActionNames.box_open_attempt],
      startDate: getServerDateWithMinuteTimeFormat(contractWithBookingInfo?.startDate),
    },
    { skip: !box?.originalEntityId },
  );

  const hasRejectedPhotos = moveOutDocs.some((doc) => doc.fileStatus === VerificationFileStatus.REJECTED);

  const isExternalBox = box?.timeToOpenLockMS != null;

  const openContractDetails = useCallback((): void => {
    contractWithBookingInfo && onOpenContractDetails(contractWithBookingInfo.contractId);
  }, [contractWithBookingInfo, onOpenContractDetails]);

  const openCancelReservationModal = useCallback((): void => {
    if (reservation) {
      dispatch(cancelReservationModalActions.openCancelReservationModal(reservation));
    }
  }, [dispatch, reservation]);

  const openTakeUpReservationModal = useCallback((): void => {
    if (reservation) {
      dispatch(takeUpReservationModalActions.openTakeUpReservationModal(reservation));
    }
  }, [dispatch, reservation]);

  const shouldHideBills = useMemo((): boolean => {
    if (contractsData) {
      const shouldHideBills = contractsData?.some((contract) => contract.discountKeys?.includes('SAVESPACE'));

      return shouldHideBills;
    }

    return false;
  }, [contractsData]);

  const renderContractInvoicingInfo = useCallback(() => {
    if (contractWithBookingInfo?.isContractPaid) {
      return contractWithBookingInfo.nextInvoiceAmount ? (
        <div className="text-center">
          <div className="text-primaryLight text-lg py-2">
            {t('Next payment')}:{' '}
            <span className="text-black font-semibold">{getClientDateFormat(dayjs(contractWithBookingInfo.nextInvoiceDueDate))}</span>
          </div>
          <div className="text-primaryLight text-lg">
            {t('Total')}:{' '}
            <span className="text-black font-semibold">
              {contractWithBookingInfo.nextInvoiceAmount} {currencySymbol}
            </span>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <div className="text-primaryLight text-lg py-2">
            {t('Calculated to')}:{' '}
            <span className="text-black font-semibold">{getClientDateFormat(dayjs(contractWithBookingInfo.chargedTo))}</span>
          </div>
        </div>
      );
    } else {
      return (
        <div className="text-center">
          <div className="text-primaryLight text-lg">
            {t('Total debt')}:{' '}
            <span className="text-error font-semibold">
              {contractWithBookingInfo?.contractDebt} {currencySymbol}
            </span>
          </div>
        </div>
      );
    }
  }, [contractWithBookingInfo, currencySymbol, t]);

  const minRentalPeriod = dayjs(reservation?.endRentDate).diff(reservation?.startRentDate, reservation?.invoiceFrequencyType);
  const minRentalPeriodValue = `${minRentalPeriod} ${t(
    minRentalPeriod === 1 ? `${reservation?.invoiceFrequencyType}` : `${reservation?.invoiceFrequencyType}s`,
  )}`;

  return (
    <div className="bg-secondaryLight rounded">
      <div className="flex flex-col px-4 py-5 bg-white rounded">
        <div className="flex flex-col gap-1">
          <div className="font-semibold text-2xl flex items-center gap-2">
            {box?.name}
            {box?.floor && <span className="text-primaryLight"> — {getLocalizedString(box.floor.name)}</span>}
            {!isReservation && <LockStatusMarker lockStatus={lockStatus} isExternalBox={isExternalBox} />}
          </div>
          <div className="flex flex-col space-y-4 desktop:justify-between desktop:flex-row desktop:space-y-0">
            <div className="flex flex-col gap-2">
              {/* <Button theme="clear" fontSize="small">
                {t('Show on map')}
              </Button> */}
              <div className="flex items-center text-primaryLight text-sm font-normal">
                <SquareIcon className="mr-2 [&>path]:fill-accent" />
                {box?.square} {getSizeCodeTypeString(box?.sizeCode?.sizeCodeType)}
              </div>
              {!isReservation ? (
                <>
                  {contractWithBookingInfo?.isContractPaid ? (
                    <div className="flex items-center text-primaryLight text-sm font-normal">
                      <BillIcon className="mr-2 stroke-accent" />
                      {contractWithBookingInfo.paidDays} {t('Paid days')}
                    </div>
                  ) : (
                    <div className="flex items-center text-error text-sm">
                      <NonePaidDays className="mr-2 stroke-error" /> 0 {t('Paid days')}
                    </div>
                  )}
                  {contractWithBookingInfo?.isContractPaid ? (
                    <div className="flex items-center text-primaryLight text-sm font-normal">
                      <AccessIcon className="mr-2 stroke-accent" />
                      {t('Access is allowed')}
                    </div>
                  ) : (
                    <div className="flex items-center text-error text-sm">
                      <AccessDeniedIcon className="mr-2 stroke-error" />
                      {t('Access is denied')}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="space-x-1.5 desktop:space-x-0 desktop:flex desktop:items-center desktop:gap-1">
                    <span className="text-accent text-sm font-normal flex-1">{t('Start rent date')}:</span>
                    <span className="text-sm text-black flex-2">{getClientDateFormat(reservation?.startRentDate)}</span>
                  </div>
                  <div className="space-x-1.5 desktop:space-x-0 desktop:flex desktop:items-center desktop:gap-1">
                    <span className="text-accent text-sm font-normal flex-1">{t('Min rental period')}:</span>
                    <span className="text-sm text-black flex-2">{minRentalPeriodValue}</span>
                  </div>
                </>
              )}
            </div>
            {reservation && isReservation ? (
              <>
                <div className="self-center desktop:self-auto">
                  <div className="text-error font-semibold text-lg py-2">
                    {t('Expires')}: <span className="text-black font-semibold">{getClientDateFormat(reservation.expirationDate)}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 desktop:w-1/5">
                  {box?.unitType !== UnitType.OFFICE && (
                    <Button icon={<ContractWhiteIcon />} iconPosition="prev" onClick={openTakeUpReservationModal}>
                      {t('Enter into contract')}
                    </Button>
                  )}
                  {box?.unitType === UnitType.OFFICE && (
                    <InfoTag className="w-full">{t('Administrator should contact you to provide instructions for rent')}</InfoTag>
                  )}
                  <Button theme="outlined" icon={<CancelIcon />} iconPosition="prev" onClick={openCancelReservationModal}>
                    {t('Cancel reservation')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {hasRejectedPhotos && (
                  <InfoTag textSize="medium" className="desktop:hidden">
                    <div onClick={openMoveOutPhotosModal} className="flex flex-col text-sm">
                      {t('Some move-out photos were rejected!')}
                      <span className="text-sm underline">{t('Click to see the reason')}</span>
                    </div>
                  </InfoTag>
                )}
                {!shouldHideBills && (
                  <div className="flex flex-col items-center gap-2 desktop:self-center">
                    {renderContractInvoicingInfo()}
                    {notifiedPriceRaise?.priceRaiseDate && (
                      <WarnTag textSize="small" className="!items-start">
                        {t(
                          'From {{date}}, the monthly rental price for your storage unit will be {{price}} {{currencySymbol}}, including VAT. The insurance fee remains unchanged.',
                          {
                            price: getPriceWithVat(notifiedPriceRaise.newPrice, boxVatRate?.ratePercent),
                            currencySymbol,
                            date: getClientDateFormat(
                              contractWithBookingInfo?.invoiceLeadDays != null
                                ? dayjs(notifiedPriceRaise.priceRaiseDate).add(contractWithBookingInfo.invoiceLeadDays + 1, 'day')
                                : notifiedPriceRaise.priceRaiseDate,
                            ),
                          },
                        )}
                      </WarnTag>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-1.5 desktop:w-1/5">
                  {hasRejectedPhotos && (
                    <InfoTag textSize="medium" className="hidden desktop:inline-flex w-full">
                      <div onClick={openMoveOutPhotosModal} className="flex flex-col text-sm cursor-pointer">
                        {t('Some move-out photos were rejected!')}
                        <span className="text-sm underline">{t('Click to see the reason')}</span>
                      </div>
                    </InfoTag>
                  )}
                  {!shouldHideBills && (
                    <Button icon={<ContractIcon />} theme="secondary" iconPosition="prev" onClick={openContractDetails}>
                      {t('Contract details')}
                    </Button>
                  )}
                  <OpenBox
                    box={box}
                    action={
                      <Button icon={<KeyIcon className="stroke-white" />} iconPosition="prev">
                        {t('Open')}
                      </Button>
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {contractWithBookingInfo?.contractId && (
        <MoveOutPhotosModal
          isOpen={isMoveOutPhotosOpen}
          onClose={closeMoveOutPhotosModal}
          contractId={contractWithBookingInfo.contractId}
          lastBoxOpening={lastBoxOpenings?.content?.[0]?.createdAt}
        />
      )}
    </div>
  );
});
